import { useState } from 'react';
import { BadgeCheck, Bike, Coins, KeyRound, Plus, Search, ShieldAlert, Star, Truck, Wallet } from 'lucide-react';
import { CourierStatus, VEHICLE_LABELS, VehicleType } from '@kick/types';
import { getErrorDetails, getErrorMessage } from '@/lib/api';
import { usePuede } from '@/lib/permisos';
import { cn, desdeHace, formatCOP } from '@/lib/cn';
import {
  useCrearDomiciliario,
  useDomiciliariosAdmin,
  useRestablecerPasswordDomiciliario,
  useVerificarDomiciliario,
  type Credenciales,
  type DomiciliarioAdmin,
} from '@/lib/queries';
import {
  Boton,
  Campo,
  ConfirmarModal,
  Cargando,
  Entrada,
  ErrorCarga,
  Modal,
  Seleccion,
  Vacio,
} from '@/components/ui';
import { useAvisos } from '@/components/avisos';
import { TarjetaCredenciales } from '@/components/TarjetaCredenciales';
import { CuentasDomiciliario, ResumenCuentas } from '@/components/CuentasDomiciliario';
import { SaldoDomiciliario } from '@/components/SaldoDomiciliario';
import { AvisoDomiciliarios } from '@/components/AvisoDomiciliarios';

const VEHICULOS: Record<string, string> = VEHICLE_LABELS;

const ESTADOS: Record<string, { texto: string; color: string }> = {
  [CourierStatus.AVAILABLE]: { texto: 'Disponible', color: 'bg-emerald-100 text-emerald-800' },
  [CourierStatus.BUSY]: { texto: 'En pedido', color: 'bg-violet-100 text-violet-800' },
  [CourierStatus.ON_BREAK]: { texto: 'En pausa', color: 'bg-amber-100 text-amber-800' },
  [CourierStatus.OFFLINE]: { texto: 'Fuera de turno', color: 'bg-kick-light text-kick-gray' },
};

export function Domiciliarios() {
  const puede = usePuede();
  const [busqueda, setBusqueda] = useState('');
  const [verificados, setVerificados] = useState('');
  const [creando, setCreando] = useState(false);
  const [credenciales, setCredenciales] = useState<Credenciales | null>(null);

  const domiciliarios = useDomiciliariosAdmin({
    q: busqueda || undefined,
    verificados: verificados || undefined,
  });

  const pendientes = domiciliarios.data?.filter((d) => !d.isVerified).length ?? 0;

  return (
    <div className="mx-auto max-w-6xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl text-kick-dark">Domiciliarios</h1>
          <p className="mt-1 text-sm text-kick-gray">
            Sin verificar no reciben pedidos, por más que su cuenta exista.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {puede('domiciliarios', 'avisar') && <AvisoDomiciliarios />}
          {puede('domiciliarios', 'crear') && (
            <Boton icono={<Plus className="h-4 w-4" />} onClick={() => setCreando(true)}>
              Nuevo domiciliario
            </Boton>
          )}
        </div>
      </div>

      {pendientes > 0 && verificados !== 'false' && (
        <button
          type="button"
          onClick={() => setVerificados('false')}
          className="mt-4 flex w-full items-center gap-3 rounded-kick border border-amber-200 bg-amber-50 px-4 py-3 text-left transition hover:bg-amber-100"
        >
          <ShieldAlert className="h-5 w-5 shrink-0 text-amber-600" />
          <span className="flex-1">
            <span className="block font-semibold text-amber-900">
              {pendientes} esperando verificación
            </span>
            <span className="block text-sm text-amber-700">
              Revisa sus documentos antes de dejarlos entrar al reparto.
            </span>
          </span>
        </button>
      )}

      <div className="mt-6 flex flex-wrap gap-2">
        <div className="relative min-w-56 flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-kick-gray" />
          <Entrada
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Nombre o usuario"
            aria-label="Buscar domiciliarios"
            className="pl-9"
          />
        </div>
        <Seleccion
          value={verificados}
          onChange={(e) => setVerificados(e.target.value)}
          aria-label="Filtrar por verificación"
          className="w-48"
        >
          <option value="">Todos</option>
          <option value="true">Verificados</option>
          <option value="false">Sin verificar</option>
        </Seleccion>
      </div>

      {domiciliarios.isLoading && <Cargando />}
      {domiciliarios.isError && (
        <ErrorCarga
          mensaje={getErrorMessage(domiciliarios.error)}
          onReintentar={() => void domiciliarios.refetch()}
        />
      )}

      {domiciliarios.data?.length === 0 && (
        <div className="mt-6">
          <Vacio
            icono={<Truck className="h-6 w-6" />}
            titulo="Ningún domiciliario"
            descripcion="Crea el primero para que empiece a recibir pedidos."
          />
        </div>
      )}

      <div className="mt-4 space-y-2">
        {domiciliarios.data?.map((domiciliario) => (
          <Fila key={domiciliario.id} domiciliario={domiciliario} />
        ))}
      </div>

      <ModalNuevo
        abierto={creando}
        onCerrar={() => setCreando(false)}
        onCreado={(c) => {
          setCreando(false);
          setCredenciales(c);
        }}
      />

      <Modal
        abierto={!!credenciales}
        onCerrar={() => setCredenciales(null)}
        titulo="Domiciliario creado"
        descripcion="Con estas credenciales entra a la app. Recuerda verificarlo para que reciba pedidos."
        ancho="sm"
      >
        <div className="p-5">
          {credenciales && <TarjetaCredenciales credenciales={credenciales} />}
          <Boton className="mt-5 w-full" onClick={() => setCredenciales(null)}>
            Ya las copié
          </Boton>
        </div>
      </Modal>
    </div>
  );
}

function Fila({ domiciliario }: { domiciliario: DomiciliarioAdmin }) {
  const puede = usePuede();
  const avisos = useAvisos();
  const verificar = useVerificarDomiciliario();
  const estado = ESTADOS[domiciliario.status];
  const [verCuentas, setVerCuentas] = useState(false);
  const [verSaldo, setVerSaldo] = useState(false);
  const restablecer = useRestablecerPasswordDomiciliario();
  const [confirmandoClave, setConfirmandoClave] = useState(false);
  const [claveNueva, setClaveNueva] = useState<Credenciales | null>(null);

  return (
    <article className="card-kick flex flex-wrap items-center gap-3 p-3.5">
      <span
        className={cn(
          'grid h-10 w-10 shrink-0 place-items-center rounded-full',
          domiciliario.isVerified
            ? 'bg-emerald-50 text-emerald-600'
            : 'bg-amber-50 text-amber-600',
        )}
      >
        <Bike className="h-4 w-4" />
      </span>

      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1.5 font-semibold text-kick-ink">
          {domiciliario.fullName}
          {domiciliario.isVerified && <BadgeCheck className="h-4 w-4 text-emerald-600" />}
        </p>
        <p className="text-sm text-kick-gray">
          <span className="font-mono">{domiciliario.username}</span>
          {' · '}
          {VEHICULOS[domiciliario.vehicleType] ?? domiciliario.vehicleType}
          {domiciliario.plate && ` ${domiciliario.plate}`}
          {' · doc. '}
          {domiciliario.documentId}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className={cn('rounded-full px-2 py-0.5 font-semibold', estado?.color)}>
          {estado?.texto ?? domiciliario.status}
        </span>
        {domiciliario.locationUpdatedAt && (
          <span className="text-kick-gray">
            posición {desdeHace(domiciliario.locationUpdatedAt)}
          </span>
        )}
        {/* Sin cuentas solo puede cobrar en efectivo: conviene que salte a la vista. */}
        <ResumenCuentas courierId={domiciliario.id} />
      </div>

      <div className="hidden text-right sm:block">
        <p className="font-semibold text-kick-ink">{domiciliario.entregas}</p>
        <p className="text-xs text-kick-gray">entregas</p>
      </div>

      {/* El saldo, en rojo cuando ya no le alcanza para trabajar. */}
      <div className="hidden text-right sm:block">
        <p
          className={cn(
            'font-semibold',
            domiciliario.balance > 0 ? 'text-kick-ink' : 'text-kick-red',
          )}
        >
          {formatCOP(domiciliario.balance)}
        </p>
        <p className="text-xs text-kick-gray">saldo</p>
      </div>

      {domiciliario.ratingCount > 0 && (
        <span className="hidden items-center gap-1 text-sm text-kick-gray sm:flex">
          <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
          {Number(domiciliario.ratingAvg).toFixed(1)}
        </span>
      )}

      <div className="flex gap-1.5">
        <Boton
          variante={domiciliario.pendingRecharges > 0 ? 'primario' : 'secundario'}
          className="relative px-2.5 py-1.5 text-xs"
          icono={<Coins className="h-3.5 w-3.5" />}
          onClick={() => setVerSaldo(true)}
        >
          Saldo
          {domiciliario.pendingRecharges > 0 && (
            <span
              className="ml-1 rounded-full bg-white/90 px-1.5 text-[10px] font-bold text-kick-red"
              aria-label={`${domiciliario.pendingRecharges} recarga(s) por revisar`}
            >
              {domiciliario.pendingRecharges}
            </span>
          )}
        </Boton>

        <Boton
          variante="secundario"
          className="px-2.5 py-1.5 text-xs"
          icono={<Wallet className="h-3.5 w-3.5" />}
          onClick={() => setVerCuentas(true)}
        >
          Cuentas
        </Boton>

        {puede('domiciliarios', 'editar') && (
          <Boton
            variante="secundario"
            className="px-2.5 py-1.5 text-xs"
            icono={<KeyRound className="h-3.5 w-3.5" />}
            onClick={() => setConfirmandoClave(true)}
          >
            Restablecer clave
          </Boton>
        )}

        {puede('domiciliarios', 'verificar') && (
        <Boton
          variante={domiciliario.isVerified ? 'peligro' : 'primario'}
          className="px-3 py-1.5 text-xs"
          cargando={verificar.isPending}
          onClick={() =>
            verificar.mutate(
              { id: domiciliario.id, isVerified: !domiciliario.isVerified },
              {
                onSuccess: () =>
                  avisos.exito(
                    domiciliario.isVerified
                      ? `${domiciliario.fullName} ya no recibe pedidos`
                      : `${domiciliario.fullName} verificado`,
                  ),
                onError: (error) => avisos.error(getErrorMessage(error)),
              },
            )
          }
        >
          {domiciliario.isVerified ? 'Revocar' : 'Verificar'}
        </Boton>
        )}
      </div>

      <ConfirmarModal
        abierto={confirmandoClave}
        titulo={`Restablecer la clave de ${domiciliario.fullName}`}
        mensaje="Se genera una contraseña temporal nueva, se cierran sus sesiones y la app le pedirá cambiarla al entrar. La clave de ahora deja de servir."
        textoConfirmar="Restablecer"
        cargando={restablecer.isPending}
        onCancelar={() => setConfirmandoClave(false)}
        onConfirmar={() =>
          restablecer.mutate(domiciliario.id, {
            onSuccess: (c) => {
              setConfirmandoClave(false);
              setClaveNueva(c);
            },
            onError: (error) => avisos.error(getErrorMessage(error)),
          })
        }
      />

      <Modal
        abierto={!!claveNueva}
        onCerrar={() => setClaveNueva(null)}
        titulo="Clave temporal nueva"
        descripcion="Pásasela al domiciliario. Solo se muestra esta vez: al entrar tendrá que cambiarla."
        ancho="md"
      >
        <div className="p-5">
          {claveNueva && <TarjetaCredenciales credenciales={claveNueva} />}
          <Boton className="mt-5 w-full" onClick={() => setClaveNueva(null)}>
            Listo
          </Boton>
        </div>
      </Modal>

      <Modal
        abierto={verSaldo}
        onCerrar={() => setVerSaldo(false)}
        titulo={`Saldo de ${domiciliario.fullName}`}
        descripcion="Recargas por revisar, historial y libro de movimientos."
        ancho="lg"
      >
        <div className="max-h-[75vh] overflow-y-auto p-5">
          {verSaldo && (
            <SaldoDomiciliario courierId={domiciliario.id} nombre={domiciliario.fullName} />
          )}
        </div>
      </Modal>

      <Modal
        abierto={verCuentas}
        onCerrar={() => setVerCuentas(false)}
        titulo={`Cuentas de ${domiciliario.fullName}`}
        descripcion="Donde los clientes le transfieren cuando no pagan en efectivo."
        ancho="md"
      >
        <div className="p-5">
          <CuentasDomiciliario courierId={domiciliario.id} nombre={domiciliario.fullName} />
        </div>
      </Modal>
    </article>
  );
}

function ModalNuevo({
  abierto,
  onCerrar,
  onCreado,
}: {
  abierto: boolean;
  onCerrar: () => void;
  onCreado: (credenciales: Credenciales) => void;
}) {
  const avisos = useAvisos();
  const crear = useCrearDomiciliario();

  const [datos, setDatos] = useState({
    username: '',
    fullName: '',
    phone: '',
    email: '',
    documentId: '',
    vehicleType: VehicleType.MOTORCYCLE as string,
    plate: '',
  });

  const errores = crear.error ? getErrorDetails(crear.error) : {};

  return (
    <Modal
      abierto={abierto}
      onCerrar={onCerrar}
      titulo="Nuevo domiciliario"
      descripcion="Nace sin verificar: puede entrar a la app pero no recibe pedidos hasta que lo apruebes."
      ancho="md"
    >
      <div className="p-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <Campo etiqueta="Usuario" requerido error={errores.username?.[0]}>
            <Entrada
              value={datos.username}
              onChange={(e) => setDatos({ ...datos, username: e.target.value.toLowerCase() })}
              placeholder="jperez"
            />
          </Campo>
          <Campo etiqueta="Nombre completo" requerido error={errores.fullName?.[0]}>
            <Entrada
              value={datos.fullName}
              onChange={(e) => setDatos({ ...datos, fullName: e.target.value })}
              placeholder="Juan Pérez Gómez"
            />
          </Campo>
          <Campo etiqueta="Teléfono" requerido error={errores.phone?.[0]}>
            <Entrada
              value={datos.phone}
              onChange={(e) => setDatos({ ...datos, phone: e.target.value })}
              placeholder="3001234567"
            />
          </Campo>
          <Campo etiqueta="Documento" requerido error={errores.documentId?.[0]}>
            <Entrada
              value={datos.documentId}
              onChange={(e) => setDatos({ ...datos, documentId: e.target.value })}
              placeholder="1017234501"
            />
          </Campo>
          <Campo etiqueta="Vehículo">
            <Seleccion
              value={datos.vehicleType}
              onChange={(e) => setDatos({ ...datos, vehicleType: e.target.value })}
            >
              {Object.entries(VEHICULOS).map(([valor, texto]) => (
                <option key={valor} value={valor}>
                  {texto}
                </option>
              ))}
            </Seleccion>
          </Campo>
          <Campo etiqueta="Placa" ayuda="Solo si el vehículo la lleva">
            <Entrada
              value={datos.plate}
              onChange={(e) => setDatos({ ...datos, plate: e.target.value.toUpperCase() })}
              placeholder="KLM45C"
            />
          </Campo>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Boton variante="secundario" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton
            cargando={crear.isPending}
            onClick={() =>
              crear.mutate(
                {
                  ...datos,
                  email: datos.email || undefined,
                  plate: datos.plate || null,
                },
                {
                  onSuccess: (respuesta) => {
                    avisos.exito(`${datos.fullName} creado`);
                    onCreado(respuesta.credenciales);
                  },
                  onError: (error) => avisos.error(getErrorMessage(error)),
                },
              )
            }
          >
            Crear domiciliario
          </Boton>
        </div>
      </div>
    </Modal>
  );
}
